// TODO: Clinic comparison state is persisted locally via AsyncStorage only.
// No server-side persistence API exists. If the user switches devices, comparison data is lost.
import { useSyncExternalStore, useEffect } from "react";
import { Platform } from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import type { ClinicVerificationStatus } from "./clinicComparison.types";

const CLINIC_COMPARISON_KEY_PREFIX = "olly_clinic_comparison_";
const MAX_COMPARISON_CLINICS = 4;

function getStorageKey(userId: string): string {
  return `${CLINIC_COMPARISON_KEY_PREFIX}${userId}`;
}

// Store shape for persistence
interface ClinicComparisonStore {
  clinicIds: string[];
  verificationStatuses: Record<string, ClinicVerificationStatus>;
}

// Simple store with subscribers
type Listener = () => void;
const listeners = new Set<Listener>();
let currentUserId: string | undefined;
let clinicIds: string[] = [];
let verificationStatuses: Record<string, ClinicVerificationStatus> = {};
let isLoading = true;

function notifyListeners() {
  listeners.forEach((listener) => listener());
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getClinicIdsSnapshot(): string[] {
  return clinicIds;
}

function getVerificationStatusesSnapshot(): Record<
  string,
  ClinicVerificationStatus
> {
  return verificationStatuses;
}

function getLoadingSnapshot(): boolean {
  return isLoading;
}

/**
 * Synchronously read from localStorage (web only)
 */
function readFromLocalStorageSync(
  userId: string,
): ClinicComparisonStore | null {
  if (Platform.OS === "web" && typeof window !== "undefined") {
    try {
      const stored = window.localStorage.getItem(getStorageKey(userId));
      if (stored) {
        return JSON.parse(stored) as ClinicComparisonStore;
      }
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Async read from AsyncStorage (native)
 */
async function loadFromAsyncStorage(userId: string) {
  isLoading = true;
  notifyListeners();

  try {
    const value = await AsyncStorage.getItem(getStorageKey(userId));
    if (value) {
      const parsed = JSON.parse(value) as ClinicComparisonStore;
      clinicIds = parsed.clinicIds || [];
      verificationStatuses = parsed.verificationStatuses || {};
    } else {
      clinicIds = [];
      verificationStatuses = {};
    }
  } catch (err) {
    console.error("Failed to load clinic comparison data:", err);
    clinicIds = [];
    verificationStatuses = {};
  }
  isLoading = false;
  notifyListeners();
}

/**
 * Persist data to storage
 */
async function persistData() {
  if (!currentUserId) return;

  const data: ClinicComparisonStore = {
    clinicIds,
    verificationStatuses,
  };

  try {
    const jsonValue = JSON.stringify(data);
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.localStorage.setItem(getStorageKey(currentUserId), jsonValue);
    }
    await AsyncStorage.setItem(getStorageKey(currentUserId), jsonValue);
  } catch (err) {
    console.error("Failed to save clinic comparison data:", err);
  }
}

/**
 * Add a clinic to comparison
 * Returns true if added successfully, false if at max or duplicate
 */
function addClinic(id: string): boolean {
  if (clinicIds.length >= MAX_COMPARISON_CLINICS) {
    return false;
  }
  if (clinicIds.includes(id)) {
    return false;
  }

  clinicIds = [...clinicIds, id];
  // Initialize status as pending (verification coming soon)
  verificationStatuses = {
    ...verificationStatuses,
    [id]: "pending",
  };
  notifyListeners();
  persistData();
  return true;
}

/**
 * Remove a clinic from comparison
 */
function removeClinic(id: string): void {
  if (!clinicIds.includes(id)) return;

  clinicIds = clinicIds.filter((clinicId) => clinicId !== id);
  const { [id]: _, ...restStatuses } = verificationStatuses;
  verificationStatuses = restStatuses;
  notifyListeners();
  persistData();
}

/**
 * Check if a clinic is in comparison
 */
function isInComparison(id: string): boolean {
  return clinicIds.includes(id);
}

/**
 * Check if more clinics can be added
 */
function canAddMore(): boolean {
  return clinicIds.length < MAX_COMPARISON_CLINICS;
}

/**
 * Get verification status for a clinic
 */
function getVerificationStatus(id: string): ClinicVerificationStatus {
  return verificationStatuses[id] || "verifying";
}

/**
 * Set verification status for a clinic
 */
function setVerificationStatus(
  id: string,
  status: ClinicVerificationStatus,
): void {
  if (!clinicIds.includes(id)) return;

  verificationStatuses = {
    ...verificationStatuses,
    [id]: status,
  };
  notifyListeners();
  persistData();
}

/**
 * Clear all comparison data (for testing/debugging)
 */
async function clearComparison() {
  if (!currentUserId) return;
  try {
    if (Platform.OS === "web" && typeof window !== "undefined") {
      window.localStorage.removeItem(getStorageKey(currentUserId));
    }
    await AsyncStorage.removeItem(getStorageKey(currentUserId));
    clinicIds = [];
    verificationStatuses = {};
    notifyListeners();
  } catch (err) {
    console.error("Failed to clear clinic comparison data:", err);
  }
}

/**
 * Hook to access clinic comparison store
 */
export function useClinicComparisonStore(userId: string | undefined) {
  // On web: read synchronously during render to prevent flash
  if (Platform.OS === "web" && userId && userId !== currentUserId) {
    currentUserId = userId;
    const stored = readFromLocalStorageSync(userId);
    if (stored) {
      clinicIds = stored.clinicIds || [];
      verificationStatuses = stored.verificationStatuses || {};
    } else {
      clinicIds = [];
      verificationStatuses = {};
    }
    isLoading = false;
  }

  // Handle userId changes and native async storage
  useEffect(() => {
    if (!userId) {
      currentUserId = undefined;
      clinicIds = [];
      verificationStatuses = {};
      isLoading = false;
      notifyListeners();
      return;
    }

    // On native: need to load async
    if (Platform.OS !== "web" && userId !== currentUserId) {
      currentUserId = userId;
      loadFromAsyncStorage(userId);
    }
  }, [userId]);

  const currentClinicIds = useSyncExternalStore(
    subscribe,
    getClinicIdsSnapshot,
    getClinicIdsSnapshot,
  );

  const currentVerificationStatuses = useSyncExternalStore(
    subscribe,
    getVerificationStatusesSnapshot,
    getVerificationStatusesSnapshot,
  );

  const isLoadingValue = useSyncExternalStore(
    subscribe,
    getLoadingSnapshot,
    getLoadingSnapshot,
  );

  return {
    comparisonClinicIds: currentClinicIds,
    verificationStatuses: currentVerificationStatuses,
    isLoading: isLoadingValue,
    addClinic,
    removeClinic,
    isInComparison,
    canAddMore,
    getVerificationStatus,
    setVerificationStatus,
    clearComparison,
  };
}
